import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef, GridEventListener } from "@mui/x-data-grid";
import clsx from "clsx";
import { InventoryCountStatus } from "@/enums";
import { baseUrl, inventoryCountStatuses } from "@/consts";
import { InventoryCount, Supplier } from "@/interfaces";
import { useDataApi } from "@/hooks";
import { MuiHelper } from "@/helpers";
import { formatName, isActive, isNonEmptyArray, isNumber } from "@/utils";
import { colors, sxButton, sxFont_12_500 } from "@/theme";
import { CrudGrid, InventoryCountEditDialog } from "@/components/organisms";
import { InventoryCountHelper } from "@/helpers/inventoryCount";
import { dateValueGetter } from "@/components/organisms/CrudGrid/helpers";
import { CrudGridRef } from "@/components/organisms/CrudGrid/interface";

const suppliersGetter = (suppliers?: Supplier[]) =>
  isNonEmptyArray(suppliers)
    ? suppliers?.map((item) => item.supplierName)?.join?.()
    : undefined;

const inventoryCountStatusTag = (inventoryCount?: InventoryCount) => {
  const inventoryCountStatus = isActive(
    inventoryCount?.dateStarted,
    inventoryCount?.dateClosed
  )
    ? InventoryCountStatus.OPEN
    : InventoryCountStatus.CLOSED;

  return (
    <Box
      sx={{
        ...sxFont_12_500,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "72px",
        height: "30px",
        borderRadius: "20px",
        color:
          inventoryCountStatus === InventoryCountStatus.OPEN
            ? colors.mutedTealGreen
            : colors.neutralGrey800,
        backgroundColor:
          inventoryCountStatus === InventoryCountStatus.OPEN
            ? colors.lightGreen
            : colors.neutralGrey300,
      }}
    >
      {inventoryCountStatuses[inventoryCountStatus]}
    </Box>
  );
};

const inventoryCountStatusRender = (inventoryCount?: InventoryCount) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
    }}
  >
    {inventoryCountStatusTag(inventoryCount)}
  </Box>
);

const columns: GridColDef[] = [
  {
    field: "name",
    headerName: "NAME",
    flex: 1,
  },
  {
    field: "dateStarted",
    headerName: "DATE STARTED",
    headerAlign: "center",
    align: "center",
    width: 130,
    type: "date",
    valueGetter: dateValueGetter,
    // valueFormatter: (value) => value ? dateValueGetter(value) : "N/A",
  },
  {
    field: "dateClosed",
    headerName: "DATE CLOSED",
    headerAlign: "center",
    align: "center",
    width: 130,
    type: "date",
    valueGetter: dateValueGetter,
    // valueFormatter: (value) => value ? value : "N/A",
  },
  {
    field: "dealerLastName",
    headerName: "CREATED BY",
    valueGetter: (value, row) => formatName(row.createdBy.split('@').at(0), value),
    width: 130,
  },
  {
    field: "suppliers",
    headerName: "SUPPLIERS",
    valueGetter: suppliersGetter,
    flex: 1,
  },
  {
    field: "numberOfProducts",
    headerName: "# OF PRODUCTS",
    type: "number",
    width: 140,
    align: "center"
  },
  {
    field: "discrepancy",
    headerName: "DISCREPANCY",
    type: "number",
    valueGetter: (_, row) => InventoryCountHelper.getDiscrepancy(row),
    width: 140,
    align: "center",
    cellClassName: (params: GridCellParams<any, number>) => {
      if (params.value == null) {
        return "";
      }

      return clsx("super-app", {
        negative: params.value < 0,
        positive: params.value > 0,
      });
    },
  },
  {
    field: "status",
    headerName: "STATUS",
    headerAlign: "center",
    width: 100,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => inventoryCountStatusRender(params.row),
  },
];

const InventoryCounts: React.FC = () => {
  const navigate = useNavigate();
  const { data: dataSupplier, isLoading: isLoadingSupplier } = useDataApi(
    baseUrl.SUPPLIER
  );
  const childRef = useRef<CrudGridRef | null>(null);
  const [isInventoryCountEditDialogOpen, setIsInventoryCountEditDialogOpen] =
    useState(false);
  const [inventoryCountToEdit, setInventoryCountToEdit] = useState<
    InventoryCount | undefined
  >(undefined);

  const reloadData = () => {
    childRef?.current?.reloadData?.();
  };

  const editInventoryCount = (inventoryCount?: InventoryCount) => {
    setInventoryCountToEdit(inventoryCount);
    setIsInventoryCountEditDialogOpen(true);
  };

  const onInventoryCountEditDialogClose = (inventoryCount?: InventoryCount) => {
    setIsInventoryCountEditDialogOpen(false);
    setInventoryCountToEdit(undefined);

    if (inventoryCount) {
      reloadData();
    }
  };

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    if (!isNumber(params?.id as number)) {
      return;
    }

    navigate(`${params?.id}`, {
      state: {
        suppliers: params.row.suppliers,
        inventoryCount: {
          inventoryCountId: params.row.inventoryCountId,
          name: params.row.name,
          dateStarted: params.row.dateStarted,
          dateClosed: params.row.dateClosed,
          createdBy: params.row.createdBy,
          createdOn: params.row.createdOn,
          dealerFirstName: params.row.dealerFirstName,
          dealerLastName: params.row.dealerLastName
        }
      }
    });
  };

  return (
    <Box
      sx={{
        "& .super-app.negative": {
          color: colors.accent3Error,
        },
        "& .super-app.positive": {
          color: colors.mutedTealGreen,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p:4,
        }}
      >
        {MuiHelper.getHeader("Inventory Counts")}
        <Box sx={{ justifySelf: "end" }}>
          <Button onClick={() => editInventoryCount()} sx={{ backgroundColor: colors.primaryOzone500, color: colors.white, px: 3, py: 1.5, borderRadius: 3, fontFamily: 'PF DIN Text Pro', fontSize: 16 }}>
            Create Count
          </Button>
        </Box>
      </Box>
      <Box sx={{ backgroundColor: colors.neutralGrey300, padding: 3 }}>
        <CrudGrid
          ref={childRef}
          baseUrl={baseUrl.INVENTORY_COUNTS}
          path="getfull"
          columns={columns}
          getRowId={(row) => row.inventoryCountId}
          setRowId={(row, id) => {
            row.inventoryCountId = id;
          }}
          onEditRow={editInventoryCount}
          onRowClick={handleRowClick}
        />
      </Box>
      <InventoryCountEditDialog
        inventoryCount={inventoryCountToEdit}
        suppliers={dataSupplier?.data}
        open={isInventoryCountEditDialogOpen}
        onClose={onInventoryCountEditDialogClose}
      />
    </Box>
  );
};

export default InventoryCounts;
